import React, { useState } from 'react';
import { Values } from '@fiverr-private/dropdown';
import { Checkbox, Combobox, ComboboxOption, ComboboxOptionGroup, ComboboxOptionLabel } from '@fiverr-private/forms';
import { Stack } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { AppI18n, appTranslate } from '../../../i18n';
import { NOT_SURE_METADATA_KEY } from '../../../constants';
import { SelectProps } from './MetadataSection';
import { NotSureDescription } from './NotSureDescription';

export const ComboboxEdit = ({
  sectionContentState,
  setSectionContentState,
  isMultiSelect,
  answerLimit,
  isMandatory,
}: SelectProps) => {
  const { isMobile } = getContext();

  const [isNotSure, setIsNotSure] = useState(sectionContentState.selectedAnswers.includes(NOT_SURE_METADATA_KEY));
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);

  const shouldDisableSearch = isMobile && sectionContentState.allAnswers.length <= 20;

  const handlePreventSearchInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    if (shouldDisableSearch) {
      e.preventDefault();
    }
  };

  const onChangeHandler = (values: Values) => {
    if (isMultiSelect) {
      const selected = values.selected as string[];
      if (selected?.length === answerLimit) {
        setIsComboboxOpen(false);
      }
    }

    setSectionContentState([(values.selected || values.item || []) as string[]].flat());
  };

  const onNotSureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSectionContentState([NOT_SURE_METADATA_KEY]);
    } else {
      setSectionContentState([]);
    }
    setIsNotSure(e.target.checked);
  };

  const options = sectionContentState.allAnswers.map(({ slug, display_name }) => (
    <ComboboxOption
      key={slug}
      value={slug}
      disabled={
        isMultiSelect &&
        sectionContentState.selectedAnswers.length >= answerLimit &&
        !sectionContentState.selectedAnswers.includes(slug)
      }
    >
      <ComboboxOptionLabel>{display_name}</ComboboxOptionLabel>
    </ComboboxOption>
  ));

  const regularCombobox = (
    <Stack onTouchStart={handlePreventSearchInteraction} onMouseDown={handlePreventSearchInteraction}>
      <Combobox
        value={
          isMultiSelect
            ? { selected: sectionContentState.selectedAnswers }
            : { item: sectionContentState.selectedAnswers[0] }
        }
        placeholder={
          sectionContentState.selectedAnswers.length < answerLimit
            ? appTranslate('sections.metadata.combobox_placeholder').toString()
            : ''
        }
        onChange={onChangeHandler}
        isOpen={isComboboxOpen}
        onOpenChanged={setIsComboboxOpen}
      >
        {isMultiSelect ? (
          <ComboboxOptionGroup isMulti name="selected">
            {options}
          </ComboboxOptionGroup>
        ) : (
          options
        )}
      </Combobox>
    </Stack>
  );

  const notSureCombobox = (
    <Combobox key="not_sure" disabled placeholder={appTranslate('sections.metadata.combobox_placeholder').toString()}>
      <ComboboxOption>&nbsp;</ComboboxOption>
    </Combobox>
  );

  return (
    <Stack direction="column" gap="4">
      {isNotSure ? notSureCombobox : regularCombobox}
      {isMandatory && (
        <Stack direction="column" gap="3">
          <Checkbox checked={isNotSure} onChange={onNotSureChange}>
            <AppI18n k="sections.metadata.im_not_sure" />
          </Checkbox>
          {isNotSure && <NotSureDescription />}
        </Stack>
      )}
    </Stack>
  );
};
